/**
 * The poll loop.
 *
 * Rather than sweeping the whole watchlist on a fixed timer, each row carries
 * its own `nextMarketAt` / `nextBazaarAt` due time. That makes per-item
 * intervals fall out for free and keeps the call rate smooth instead of
 * bursting every N seconds — a burst is what trips Torn's code 5.
 *
 * A 1-second ticker asks "what is due, and can we afford it?", dispatches what
 * fits in the budget, and re-queues the rest for the next second.
 */

import { fetchItemMarket, fetchUserBazaar, TORN_CACHE_DELAY_SEC } from "../api/torn.ts";
import { fetchBazaarListings, fetchRankedListings, type RankedTab } from "../api/weav3r.ts";
import { acquireKey, hasBudget, isGloballyPaused, withKey } from "../api/keyring.ts";
import { isAbort } from "../api/http.ts";
import { Observable, ranked$, settings$, watchlist$ } from "../state/store.ts";
import type {
  Offer,
  RankedRowState,
  RankedWatch,
  RowState,
  Settings,
  WatchItem,
} from "../state/types.ts";
import {
  cheapest,
  dealTier,
  effectiveIntervalSec,
  effectiveMinUnits,
  feeRate,
  margin,
  offerKey,
  offerQualifies,
} from "./scoring.ts";
import { claimAlert, isIgnored, resetAlerts } from "./dedupe.ts";
import { mergeCandidates, rankedMatches, rankedOfferKey, tornBonusParam } from "./ranked.ts";
import { playDealSound } from "../alerts/sound.ts";
import { notifyDeal } from "../alerts/notify.ts";
import { buyUrl } from "../ui/links.ts";

export type EngineStatus = "stopped" | "running" | "starving";

export interface EngineState {
  status: EngineStatus;
  lastTickAt: number | null;
  lastError: string | null;
  /** Set when the loop is up but cannot spend — no keys, or all cooling down. */
  blockedReason: string | null;
}

export const engine$ = new Observable<EngineState>({
  status: "stopped",
  lastTickAt: null,
  lastError: null,
  blockedReason: null,
});

export const rows$ = new Observable<Map<number, RowState>>(new Map());
export const rankedRows$ = new Observable<Map<string, RankedRowState>>(new Map());

/** Cheapest green deal on screen, for the "jump to best deal" hotkey. */
export const bestDeal$ = new Observable<{ itemId: number; offer: Offer } | null>(null);

interface Due {
  marketAt: number;
  bazaarAt: number;
}

const dueTimes = new Map<number, Due>();
const rankedDue = new Map<string, number>();

let ticker: number | null = null;
let abortController: AbortController | null = null;

/** Rows currently in flight, so a slow call is never dispatched twice. */
const inFlight = new Set<string>();

/**
 * Verification calls are opportunistic, not mandatory — cap them per tick so a
 * badly set profit % turning every row green cannot eat the whole budget.
 */
const MAX_VERIFICATIONS_PER_TICK = 3;

function emptyRow(): RowState {
  return {
    best: null,
    tier: null,
    margin: null,
    marketBest: null,
    bazaarBest: null,
    bazaarAverage: null,
    error: null,
    lastMarketAt: null,
    lastBazaarAt: null,
    loading: false,
  };
}

function patchRow(itemId: number, patch: Partial<RowState>): void {
  rows$.update((rows) => {
    const next = new Map(rows);
    next.set(itemId, { ...(next.get(itemId) ?? emptyRow()), ...patch });
    return next;
  });
}

function patchRankedRow(id: string, patch: Partial<RankedRowState>): void {
  rankedRows$.update((rows) => {
    const next = new Map(rows);
    const current = next.get(id) ?? {
      candidates: [],
      error: null,
      lastCheckedAt: null,
      loading: false,
    };
    next.set(id, { ...current, ...patch });
    return next;
  });
}

/**
 * Torn caches market and bazaar data globally for 30 seconds, so anything
 * faster spends quota for identical data. The UI warns; this enforces.
 */
export function effectiveTornInterval(item: WatchItem, settings: Settings): number {
  return Math.max(TORN_CACHE_DELAY_SEC, effectiveIntervalSec(item, settings));
}

function bazaarInterval(item: WatchItem, settings: Settings): number {
  const ratio = Math.max(1, Math.trunc(settings.bazaarTickRatio || 1));
  return effectiveTornInterval(item, settings) * ratio;
}

/** Drops due-time bookkeeping for rows that are no longer watched. */
function reconcile(): void {
  const watched = new Set(watchlist$.get().map((item) => item.id));
  for (const id of [...dueTimes.keys()]) if (!watched.has(id)) dueTimes.delete(id);

  const rankedIds = new Set(ranked$.get().map((watch) => watch.id));
  for (const id of [...rankedDue.keys()]) if (!rankedIds.has(id)) rankedDue.delete(id);

  rows$.update((rows) => {
    const next = new Map<number, RowState>();
    for (const [id, state] of rows) if (watched.has(id)) next.set(id, state);
    return next;
  });

  rankedRows$.update((rows) => {
    const next = new Map<string, RankedRowState>();
    for (const [id, state] of rows) if (rankedIds.has(id)) next.set(id, state);
    return next;
  });
}

function recomputeRow(item: WatchItem, settings: Settings): void {
  const state = rows$.get().get(item.id) ?? emptyRow();
  const fee = feeRate(settings);

  const pool: Offer[] = [];
  if (state.marketBest) pool.push(state.marketBest);
  if (settings.bazaarEnabled && state.bazaarBest) pool.push(state.bazaarBest);

  const qualifying = pool.filter((offer) => offerQualifies(offer, item, settings, isIgnored));
  const best = cheapest(qualifying);

  if (!best) {
    patchRow(item.id, { best: null, tier: null, margin: null });
    return;
  }

  patchRow(item.id, {
    best,
    tier: dealTier(best.price, item.marketValue, settings.profitPct, item.maxBuyPrice, fee),
    margin: margin(best.price, item.marketValue, fee),
  });
}

/** Recomputes every row without re-fetching. Used when a setting changes. */
export function recomputeAll(): void {
  const settings = settings$.get();
  for (const item of watchlist$.get()) recomputeRow(item, settings);
  refreshBestDeal();
}

function refreshBestDeal(): void {
  let winner: { itemId: number; offer: Offer } | null = null;
  for (const [itemId, state] of rows$.get()) {
    if (state.tier !== "green" || !state.best) continue;
    if (!winner || (state.margin ?? 0) < (rows$.get().get(winner.itemId)?.margin ?? 0)) {
      winner = { itemId, offer: state.best };
    }
  }
  bestDeal$.set(winner);
}

/**
 * Confirms a bazaar hit straight from Torn before alerting.
 *
 * weav3r's cache can be ~60s stale, which on a popular item is long enough for
 * the listing to be gone. One extra call turns "probably a deal" into "still
 * there at that price".
 */
async function verifyBazaarOffer(offer: Offer, signal: AbortSignal): Promise<Offer | null> {
  if (offer.sellerId === null) return offer;

  try {
    const live = await withKey((key) => fetchUserBazaar(offer.sellerId as number, offer.itemId, key, signal));
    // No key with budget: fall back to trusting weav3r rather than going silent.
    if (live === null) return offer;

    const match = live.find((candidate) => candidate.price <= offer.price);
    if (!match) return null;
    return { ...match, sellerName: offer.sellerName, lastChecked: null };
  } catch (err) {
    if (isAbort(err)) return null;
    // Verification is best-effort; a failure should not suppress the alert.
    return offer;
  }
}

let verificationsThisTick = 0;

async function maybeAlert(item: WatchItem, settings: Settings, signal: AbortSignal): Promise<void> {
  const state = rows$.get().get(item.id);
  if (!state || state.tier !== "green" || !state.best) return;

  let offer = state.best;
  const key = offerKey(offer);
  if (!claimAlert(key)) return;

  if (
    settings.bazaarVerify &&
    offer.source === "bazaar" &&
    verificationsThisTick < MAX_VERIFICATIONS_PER_TICK
  ) {
    verificationsThisTick += 1;
    const verified = await verifyBazaarOffer(offer, signal);
    if (!verified) {
      // Gone before we got there. Drop it from the row so the UI stops
      // advertising a listing that no longer exists.
      patchRow(item.id, { bazaarBest: null });
      recomputeRow(item, settings);
      return;
    }
    offer = verified;
  }

  if (settings.soundEnabled) playDealSound();
  if (settings.notifEnabled) {
    notifyDeal({
      itemId: item.id,
      itemName: item.name,
      price: offer.price,
      marketValue: item.marketValue,
      source: offer.source === "bazaar" ? "Bazaar" : "Item Market",
      url: buyUrl(offer),
      sellerName: offer.sellerName,
    });
  }
}

async function pollMarket(item: WatchItem, settings: Settings, signal: AbortSignal): Promise<void> {
  const guard = `m:${item.id}`;
  if (inFlight.has(guard)) return;
  inFlight.add(guard);
  patchRow(item.id, { loading: true });

  try {
    const result = await withKey((key) =>
      fetchItemMarket(item.id, key, { signal, limit: 100 }),
    );
    if (result === null) return;

    const minUnits = effectiveMinUnits(item, settings);
    const usable = result.offers.filter(
      (offer) => offer.quantity === 0 || offer.quantity >= minUnits,
    );

    patchRow(item.id, {
      marketBest: cheapest(usable),
      lastMarketAt: Date.now(),
      error: null,
    });

    // Torn's own average is fresher than the cached catalog's copy.
    if (result.averagePrice > 0 && result.averagePrice !== item.marketValue) {
      watchlist$.update((items) =>
        items.map((row) => (row.id === item.id ? { ...row, marketValue: result.averagePrice } : row)),
      );
    }
  } catch (err) {
    if (isAbort(err)) return;
    patchRow(item.id, { error: err instanceof Error ? err.message : String(err) });
  } finally {
    inFlight.delete(guard);
    patchRow(item.id, { loading: false });
  }
}

async function pollBazaar(item: WatchItem, settings: Settings, signal: AbortSignal): Promise<void> {
  const guard = `b:${item.id}`;
  if (inFlight.has(guard)) return;
  inFlight.add(guard);

  try {
    const result = await fetchBazaarListings(item.id, {
      signal,
      minQty: effectiveMinUnits(item, settings),
      maxPrice: item.maxBuyPrice,
    });

    patchRow(item.id, {
      bazaarBest: cheapest(result.offers),
      bazaarAverage: result.bazaarAverage,
      lastBazaarAt: Date.now(),
    });
  } catch (err) {
    if (isAbort(err)) return;
    // weav3r is third-party and optional — note it, keep polling the market.
    patchRow(item.id, { error: `Bazaars: ${err instanceof Error ? err.message : String(err)}` });
  } finally {
    inFlight.delete(guard);
  }
}

function rankedTabFor(itemType: string): RankedTab {
  return itemType === "Weapon" ? "weapons" : "armor";
}

async function pollRanked(watch: RankedWatch, signal: AbortSignal): Promise<void> {
  const guard = `r:${watch.id}`;
  if (inFlight.has(guard)) return;
  inFlight.add(guard);
  patchRankedRow(watch.id, { loading: true });

  try {
    const weav3rOffers = await fetchRankedListings(
      watch.itemId,
      watch.itemName,
      rankedTabFor(watch.itemType),
      { signal, maxPrice: watch.maxPrice },
    ).catch(() => [] as Offer[]);

    const bonus = tornBonusParam(watch);
    const tornResult = await withKey((key) =>
      fetchItemMarket(watch.itemId, key, { signal, limit: 100, bonus }),
    );
    const tornOffers = (tornResult?.offers ?? []).filter((offer) => offer.details !== null);

    const merged = mergeCandidates(tornOffers, weav3rOffers).filter((offer) =>
      rankedMatches(offer, watch),
    );

    patchRankedRow(watch.id, {
      candidates: merged,
      lastCheckedAt: Date.now(),
      error: null,
    });

    const settings = settings$.get();
    const top = merged[0];
    if (top && claimAlert(rankedOfferKey(top))) {
      if (settings.soundEnabled) playDealSound();
      if (settings.notifEnabled) {
        notifyDeal({
          itemId: watch.itemId,
          itemName: watch.itemName,
          price: top.price,
          marketValue: 0,
          source: top.source === "bazaar" ? "Bazaar (ranked)" : "Item Market (ranked)",
          url: buyUrl(top),
          sellerName: top.sellerName,
        });
      }
    }
  } catch (err) {
    if (isAbort(err)) return;
    patchRankedRow(watch.id, { error: err instanceof Error ? err.message : String(err) });
  } finally {
    inFlight.delete(guard);
    patchRankedRow(watch.id, { loading: false });
  }
}

async function tick(): Promise<void> {
  const signal = abortController?.signal;
  if (!signal || signal.aborted) return;

  const now = Date.now();
  const settings = settings$.get();
  const items = watchlist$.get();
  const rankedWatches = ranked$.get();

  verificationsThisTick = 0;
  reconcile();

  if (isGloballyPaused(now)) {
    engine$.update((state) => ({
      ...state,
      status: "starving",
      blockedReason: "Torn returned an IP block — paused briefly.",
    }));
    return;
  }

  if (items.length === 0 && rankedWatches.length === 0) {
    engine$.update((state) => ({ ...state, status: "running", blockedReason: null }));
    return;
  }

  if (!hasBudget(now)) {
    engine$.update((state) => ({
      ...state,
      status: "starving",
      blockedReason:
        acquireKey(now) === null && items.length > 0
          ? "Out of API budget — add a key from another account or raise the interval."
          : null,
    }));
    // weav3r costs no Torn quota, so bazaar polling continues regardless.
  } else {
    engine$.update((state) => ({ ...state, status: "running", blockedReason: null }));
  }

  const work: Array<Promise<void>> = [];

  for (const item of items) {
    const due = dueTimes.get(item.id) ?? { marketAt: 0, bazaarAt: 0 };

    if (now >= due.marketAt && hasBudget(now)) {
      due.marketAt = now + effectiveTornInterval(item, settings) * 1000;
      work.push(
        pollMarket(item, settings, signal).then(async () => {
          recomputeRow(item, settings);
          await maybeAlert(item, settings, signal);
        }),
      );
    }

    if (settings.bazaarEnabled && now >= due.bazaarAt) {
      due.bazaarAt = now + bazaarInterval(item, settings) * 1000;
      work.push(
        pollBazaar(item, settings, signal).then(async () => {
          recomputeRow(item, settings);
          await maybeAlert(item, settings, signal);
        }),
      );
    }

    dueTimes.set(item.id, due);
  }

  for (const watch of rankedWatches) {
    const dueAt = rankedDue.get(watch.id) ?? 0;
    if (now < dueAt) continue;
    // Ranked lists move slowly; polling them as hard as a stackable item wastes quota.
    rankedDue.set(watch.id, now + Math.max(60, settings.intervalSec * 2) * 1000);
    work.push(pollRanked(watch, signal));
  }

  if (work.length > 0) {
    await Promise.allSettled(work);
    refreshBestDeal();
  }

  engine$.update((state) => ({ ...state, lastTickAt: Date.now() }));
}

export function isRunning(): boolean {
  return ticker !== null;
}

export function start(): void {
  if (ticker !== null) return;

  abortController = new AbortController();
  resetAlerts();
  engine$.update((state) => ({ ...state, status: "running", lastError: null, blockedReason: null }));

  // Nothing is due yet on a fresh start, so fire immediately then settle into
  // the 1s cadence.
  void tick();
  ticker = window.setInterval(() => {
    void tick().catch((err) => {
      engine$.update((state) => ({
        ...state,
        lastError: err instanceof Error ? err.message : String(err),
      }));
    });
  }, 1000);
}

export function stop(): void {
  if (ticker !== null) {
    window.clearInterval(ticker);
    ticker = null;
  }
  abortController?.abort();
  abortController = null;
  dueTimes.clear();
  rankedDue.clear();
  inFlight.clear();
  resetAlerts();
  engine$.update((state) => ({ ...state, status: "stopped", blockedReason: null }));
}

export function toggle(): void {
  if (isRunning()) stop();
  else start();
}

/** Forces every row to re-poll on the next tick. */
export function pokeAll(): void {
  for (const due of dueTimes.values()) {
    due.marketAt = 0;
    due.bazaarAt = 0;
  }
  for (const id of rankedDue.keys()) rankedDue.set(id, 0);
}
